/**
 * What the words in a member's readings mean, ported from the prototype's
 * Learn tab. The chart panel and the daily guidance both print "Mahadasha",
 * "Antardasha", "nakshatra" and "pada" and have never once said what they are.
 *
 * Copy is CODE, like the chart reading copy in `services/astro/copy.ts`: a fix
 * written here reaches every tenant on the next deploy. Nothing is seeded into
 * the database.
 */

/** The one-paragraph answer to "what am I even looking at?". */
export const VEDIC_INTRO =
  'A system from India that reads the sky at the moment you were born, and uses that reading to understand how a life tends to unfold. It is offered here as a reflective framework, never as a prediction.';

export type VedicPoint = { title: string; body: string };

/** What the system is and is not, in four points. */
export const VEDIC_POINTS: readonly VedicPoint[] = [
  {
    title: 'It starts from your exact birth moment',
    body: 'Vedic astrology uses the precise date, time and place you were born to map where every planet stood. That snapshot is the chart everything else is read from, which is why the app asks for all three.',
  },
  {
    title: 'It is about timing as much as character',
    body: 'Unlike most horoscopes, it pays attention to when things tend to come around. It tracks cycles, called dashas, that can run for months or years, each one bringing a different set of themes into focus.',
  },
  {
    title: 'It is meant to be used on ordinary decisions',
    body: 'Which months suit a big move, when to hold back, where your natural strengths sit and where you tend to trip. It gives you a frame for a judgement call, and the call stays yours.',
  },
  {
    title: 'It is a map, not a forecast',
    body: 'It does not say what will happen. It describes the terrain, so you can walk it with more awareness and less guesswork. Nothing in this app is a guarantee of an outcome.',
  },
];

export type VedicChartSection = { label: string; body: string };

/** How to read each block the chart and guidance screens actually show. */
export const VEDIC_CHART_SECTIONS: readonly VedicChartSection[] = [
  {
    label: 'Daily guidance',
    body: 'Your snapshot for today: which theme is active, what the day is best spent on, and what to take care with.',
  },
  {
    label: 'Rising sign',
    body: 'How you tend to approach life, money and decisions. It is the lens the rest of the chart is read through.',
  },
  {
    label: 'Wealth houses',
    body: 'The 2nd house is what you earn and keep. The 11th is gains, networks and income that recurs.',
  },
  {
    label: 'Moon nakshatra',
    body: 'Your emotional nature and your instincts about money, taken from the Moon’s precise position at birth.',
  },
  {
    label: 'Time cycle (dasha)',
    body: 'The planetary period you are in now. Each one carries its own themes, opportunities and pressures.',
  },
  {
    label: 'Transits',
    body: 'Where the planets are right now, and how those positions meet the chart you were born with.',
  },
];

export type GlossaryEntry = { term: string; definition: string };

/**
 * The terms, grouped so related ones sit together rather than alphabetically:
 * the three dasha words, then the two Moon words, then the two names for the
 * rising sign. Someone reading their own chart meets them in roughly this
 * order, and a definition is worth more next to its siblings.
 */
export const VEDIC_GLOSSARY: readonly GlossaryEntry[] = [
  {
    term: 'Mahadasha',
    definition:
      'Your main planetary cycle. Each planet takes a turn ruling a stretch of your life, for a set number of years, and shapes the overall theme of that period.',
  },
  {
    term: 'Antardasha',
    definition:
      'A shorter sub-cycle inside the main one. It lays a second layer of energy over the Mahadasha and usually runs a few months.',
  },
  {
    term: 'Dasha',
    definition:
      'A planetary time period. Vedic astrology divides a life into cycles, each ruled by a different planet with its own themes.',
  },
  {
    term: 'Nakshatra',
    definition:
      'One of 27 lunar star clusters. It places your Moon more precisely than a Moon sign alone can.',
  },
  {
    term: 'Pada',
    definition:
      'One of four quarters inside a nakshatra. Each carries a slightly different quality and adds nuance to where your Moon sits.',
  },
  {
    term: 'Ascendant',
    definition:
      'Your rising sign: the sign on the eastern horizon at the moment you were born. It is one of the most important points in a chart.',
  },
  {
    term: 'Lagna',
    definition:
      'The Sanskrit word for the ascendant, your rising sign. The two mean the same thing.',
  },
  {
    term: 'Transit',
    definition:
      'Where a planet is in the sky right now, and how that position meets the chart you were born with.',
  },
  {
    term: 'Yoga',
    definition:
      'A named combination of planets. When certain placements line up in a particular way they form a yoga, a pattern read as having its own effect.',
  },
  {
    term: 'Rahu',
    definition:
      'The Moon’s north node. Read as the direction you are growing toward: new ground, ambition, the thing you are here to develop.',
  },
  {
    term: 'Ketu',
    definition:
      'The Moon’s south node. Read as what already comes naturally to you, sometimes so naturally that it makes growth harder.',
  },
  {
    term: 'Lord',
    definition:
      'The planet that rules a sign. Saying a planet is the lord of a house means it carries particular influence over that area of life.',
  },
  {
    term: 'House',
    definition:
      'One of 12 sections of a birth chart. Each stands for an area of life: money, relationships, work, health and so on.',
  },
];
